/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package guide

import i18n.StringKey

/**
 * Which app screen a contextual tour is being shown for. Rendering is platform-specific;
 * the step list is shared so web/iOS/Android use the exact same tour content.
 */
enum class GuideScreen {
    TRANSACTIONS,
    DASHBOARD_STANDARD,
    DASHBOARD_GRID,
    GOALS,
    BUDGET,
    /** Allocation Studio modal — launched from its own in-modal 'i' button
     *  after the user clicks a goal card on the Goals page. */
    STUDIO,
    /** Debt Paydown Studio modal — launched from its own 'i' button when
     *  the user opens a debt-payoff goal (separate component from the
     *  general allocation Studio). */
    STUDIO_DEBT,
}

/**
 * One step of a tour. [anchorId] is looked up by the platform-specific renderer to
 * find the visible element the bubble points at (web: `data-guide="<anchorId>"`;
 * native: a matching key passed to the overlay's anchor registry).
 */
data class GuideStep(
    val anchorId: String,
    val titleKey: StringKey,
    val bodyKey: StringKey,
)

/**
 * Single source of truth for tour content. Adding a new screen = adding an entry here
 * + matching StringKeys in the i18n files + tagging the anchors on each platform.
 */
object GuideRegistry {
    /**
     * @param wiz3rEnabled set to true when the user is currently seeing Wiz 3R
     *   personalized cards (i.e. cards with a populated Reveal/Reason/Realize
     *   narrative). On the Goals tour, this swaps the personalized-insights
     *   step's body to a 3R-aware explanation instead of the legacy
     *   "AI-generated suggestions" one.
     */
    fun stepsFor(screen: GuideScreen, wiz3rEnabled: Boolean = false): List<GuideStep> =
        when (screen) {
            GuideScreen.TRANSACTIONS ->
                listOf(
                    GuideStep("nav-tabs", StringKey.TOUR_TXN_NAV_TITLE, StringKey.TOUR_TXN_NAV_BODY),
                    GuideStep("sync-button", StringKey.TOUR_TXN_SYNC_TITLE, StringKey.TOUR_TXN_SYNC_BODY),
                    GuideStep("account-selector", StringKey.TOUR_TXN_ACCOUNTS_TITLE, StringKey.TOUR_TXN_ACCOUNTS_BODY),
                    GuideStep("txn-row", StringKey.TOUR_TXN_ROW_TITLE, StringKey.TOUR_TXN_ROW_BODY),
                    GuideStep("txn-icon", StringKey.TOUR_TXN_MULTISELECT_TITLE, StringKey.TOUR_TXN_MULTISELECT_BODY),
                    GuideStep("ai-badge", StringKey.TOUR_TXN_AI_TITLE, StringKey.TOUR_TXN_AI_BODY),
                    GuideStep("category-text", StringKey.TOUR_TXN_MODIFY_TITLE, StringKey.TOUR_TXN_MODIFY_BODY),
                )
            GuideScreen.DASHBOARD_STANDARD ->
                listOf(
                    GuideStep("savings-rate-card", StringKey.TOUR_DASH_SAVINGS_RATE_TITLE, StringKey.TOUR_DASH_SAVINGS_RATE_BODY),
                    GuideStep("monthly-bars", StringKey.TOUR_DASH_MONTHLY_BARS_TITLE, StringKey.TOUR_DASH_MONTHLY_BARS_BODY),
                    GuideStep("account-selector", StringKey.TOUR_DASH_ACCOUNTS_TITLE, StringKey.TOUR_DASH_ACCOUNTS_BODY),
                    GuideStep("chart-type-selector", StringKey.TOUR_DASH_CHART_TYPE_TITLE, StringKey.TOUR_DASH_CHART_TYPE_BODY),
                    GuideStep("active-chart", StringKey.TOUR_DASH_ACTIVE_CHART_TITLE, StringKey.TOUR_DASH_ACTIVE_CHART_BODY),
                    GuideStep("recommendation-panel", StringKey.TOUR_DASH_RECOMMENDATION_TITLE, StringKey.TOUR_DASH_RECOMMENDATION_BODY),
                    GuideStep("menu-button", StringKey.TOUR_DASH_GRID_HINT_TITLE, StringKey.TOUR_DASH_GRID_HINT_BODY),
                )
            // Pro "Customize" grid layout: same core steps as Standard, minus the
            // chart-type selector (each chart is its own widget in Pro), plus a
            // step about dragging/resizing widgets.
            // Grid mode has no "active chart" step — each chart is its own widget, so
            // there's no single selected-chart slot to describe.
            GuideScreen.DASHBOARD_GRID ->
                // Two-act order:
                //   Act 1 (Pro/customize features) — customize → save → lock →
                //     switch (auto-back to default after demo) → drag/resize
                //   Act 2 (your data) — savings KPI → account filter →
                //     monthly bars → savings trend → expense timeline
                //
                // The panel starts CLOSED and AUTO-OPENS on the customize-
                // toolbar step (handled in WebDashboardRedesigned's listener
                // for the 'wiz3r-demo-open-customize' event GuideOverlay
                // dispatches). That lets the camera SEE the panel slide
                // open as the bubble lands on Customize.
                listOf(
                    // ── Act 1: Pro/customize features ───────────────────
                    GuideStep("customize-toolbar", StringKey.TOUR_DASH_CUSTOMIZE_TITLE, StringKey.TOUR_DASH_CUSTOMIZE_BODY),
                    GuideStep("layouts-save", StringKey.TOUR_DASH_LAYOUTS_SAVE_TITLE, StringKey.TOUR_DASH_LAYOUTS_SAVE_BODY),
                    GuideStep("layout-lock", StringKey.TOUR_DASH_LAYOUT_LOCK_TITLE, StringKey.TOUR_DASH_LAYOUT_LOCK_BODY),
                    // layouts-switch: dashboard auto-clicks the second
                    // chip, holds, then clicks back to the first so the
                    // camera sees the swap-and-return arc.
                    GuideStep("layouts-switch", StringKey.TOUR_DASH_LAYOUTS_SWITCH_TITLE, StringKey.TOUR_DASH_LAYOUTS_SWITCH_BODY),
                    // grid-canvas: forces handles visible on target widget
                    // + fake cursor "drags" the SE corner during a gradual
                    // resize-and-restore (see performGridCanvasDemoResize).
                    GuideStep("grid-canvas", StringKey.TOUR_DASH_GRID_CANVAS_TITLE, StringKey.TOUR_DASH_GRID_CANVAS_BODY),

                    // ── Act 2: your data ────────────────────────────────
                    GuideStep("savings-rate-card", StringKey.TOUR_DASH_SAVINGS_RATE_TITLE, StringKey.TOUR_DASH_SAVINGS_RATE_BODY),
                    GuideStep("account-selector", StringKey.TOUR_DASH_ACCOUNTS_TITLE, StringKey.TOUR_DASH_ACCOUNTS_BODY),
                    GuideStep("monthly-bars", StringKey.TOUR_DASH_MONTHLY_BARS_TITLE, StringKey.TOUR_DASH_MONTHLY_BARS_BODY),
                    GuideStep("savings-trend", StringKey.TOUR_DASH_SAVINGS_TREND_TITLE, StringKey.TOUR_DASH_SAVINGS_TREND_BODY),
                    GuideStep("expense-timeline", StringKey.TOUR_DASH_EXPENSE_TIMELINE_TITLE, StringKey.TOUR_DASH_EXPENSE_TIMELINE_BODY),
                )
            GuideScreen.GOALS ->
                listOf(
                    GuideStep("add-goal-button", StringKey.TOUR_GOALS_ADD_TITLE, StringKey.TOUR_GOALS_ADD_BODY),
                    GuideStep("goal-card", StringKey.TOUR_GOALS_CARD_TITLE, StringKey.TOUR_GOALS_CARD_BODY),
                    GuideStep(
                        "personalized-insights",
                        StringKey.TOUR_GOALS_INSIGHTS_TITLE,
                        // Pro users with Wiz 3R get a body that names the 3R
                        // framework; non-AI users keep the original wording.
                        if (wiz3rEnabled) StringKey.TOUR_GOALS_INSIGHTS_BODY_WIZ3R
                        else StringKey.TOUR_GOALS_INSIGHTS_BODY,
                    ),
                    GuideStep("tips-search", StringKey.TOUR_GOALS_TIPS_SEARCH_TITLE, StringKey.TOUR_GOALS_TIPS_SEARCH_BODY),
                    GuideStep("pin-icon", StringKey.TOUR_GOALS_PIN_TITLE, StringKey.TOUR_GOALS_PIN_BODY),
                )
            // Allocation Studio modal — its own 'i' button launches this tour.
            // Anchors live on the section host divs (#studioHeader / #studioForm
            // / etc) that WebStudioComponent renders into.
            GuideScreen.STUDIO ->
                listOf(
                    GuideStep("studio-header", StringKey.TOUR_STUDIO_HEADER_TITLE, StringKey.TOUR_STUDIO_HEADER_BODY),
                    GuideStep("studio-progress", StringKey.TOUR_STUDIO_PROGRESS_TITLE, StringKey.TOUR_STUDIO_PROGRESS_BODY),
                    GuideStep("studio-strip", StringKey.TOUR_STUDIO_STRIP_TITLE, StringKey.TOUR_STUDIO_STRIP_BODY),
                    GuideStep("studio-form", StringKey.TOUR_STUDIO_FORM_TITLE, StringKey.TOUR_STUDIO_FORM_BODY),
                    GuideStep("studio-chart", StringKey.TOUR_STUDIO_CHART_TITLE, StringKey.TOUR_STUDIO_CHART_BODY),
                    GuideStep("studio-savings-rate", StringKey.TOUR_STUDIO_SAVINGS_RATE_TITLE, StringKey.TOUR_STUDIO_SAVINGS_RATE_BODY),
                    GuideStep("studio-footer", StringKey.TOUR_STUDIO_FOOTER_TITLE, StringKey.TOUR_STUDIO_FOOTER_BODY),
                )
            // Debt Paydown Studio modal (separate component from the
            // allocation Studio above). Anchors live on the section host
            // divs in WebDebtStudioComponent.renderInto.
            GuideScreen.STUDIO_DEBT ->
                listOf(
                    GuideStep("debt-studio-header", StringKey.TOUR_DEBT_STUDIO_HEADER_TITLE, StringKey.TOUR_DEBT_STUDIO_HEADER_BODY),
                    GuideStep("debt-studio-goal-data", StringKey.TOUR_DEBT_STUDIO_GOAL_TITLE, StringKey.TOUR_DEBT_STUDIO_GOAL_BODY),
                    GuideStep("debt-studio-strip", StringKey.TOUR_DEBT_STUDIO_STRIP_TITLE, StringKey.TOUR_DEBT_STUDIO_STRIP_BODY),
                    GuideStep("debt-studio-levers", StringKey.TOUR_DEBT_STUDIO_LEVERS_TITLE, StringKey.TOUR_DEBT_STUDIO_LEVERS_BODY),
                    GuideStep("debt-studio-chart", StringKey.TOUR_DEBT_STUDIO_CHART_TITLE, StringKey.TOUR_DEBT_STUDIO_CHART_BODY),
                    GuideStep("debt-studio-footer", StringKey.TOUR_DEBT_STUDIO_FOOTER_TITLE, StringKey.TOUR_DEBT_STUDIO_FOOTER_BODY),
                )
            GuideScreen.BUDGET ->
                listOf(
                    GuideStep("year-selector", StringKey.TOUR_BUDGET_YEAR_TITLE, StringKey.TOUR_BUDGET_YEAR_BODY),
                    GuideStep("income-section", StringKey.TOUR_BUDGET_INCOME_TITLE, StringKey.TOUR_BUDGET_INCOME_BODY),
                    GuideStep("expense-section", StringKey.TOUR_BUDGET_EXPENSE_TITLE, StringKey.TOUR_BUDGET_EXPENSE_BODY),
                    GuideStep("budget-cell", StringKey.TOUR_BUDGET_CELL_TITLE, StringKey.TOUR_BUDGET_CELL_BODY),
                    GuideStep("copy-prev-year", StringKey.TOUR_BUDGET_COPY_YEAR_TITLE, StringKey.TOUR_BUDGET_COPY_YEAR_BODY),
                    GuideStep("net-savings-row", StringKey.TOUR_BUDGET_NET_TITLE, StringKey.TOUR_BUDGET_NET_BODY),
                )
        }
}
